import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoChevronForward } from "react-icons/io5";
import { DEVICE_TYPES, MODELS, TRAITS, hasTrait, isModel, isType } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { useToggleTop } from "../../../components/common/ToggleTop";
import AppModal from "../../../components/layout/app/AppModal";
import {
  DEFAULT_ERROR,
  MotorTabs,
  SettingsPage,
  SettingsSkeleton,
  UPDATED,
  Unavailable,
  goBack,
  readConfigDevice,
  settingsPayload,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";

const FORM_ID = "hx-set-curtain-form";
const INPUT_ID = "hx-set-curtain-seconds";
const TABS_ID = "hx-set-curtain-tab";
const PANEL_ID = "hx-set-curtain-panel";

// Each motor's travel time in ms (deviceSettings[0] and [1]); the app shows
// 10 s when none is stored.
const readTimes = (source) =>
  [0, 1].map((index) => {
    const ms = Number(source?.deviceSettings?.[index]?.settings?.maxDurationMs ?? 10000);
    return Number.isFinite(ms) ? ms : 10000;
  });

// Quick Calibration for curtain modules with trait 902
// (app/screens/DeviceSettings/CurtainSettings.js): W1CM_1 has one curtain,
// W2CM_1 has Motor 1 and Motor 2 tabs.
function CurtainSettings() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const dual = isModel(device, MODELS.W2CM_1);
  const supported =
    isType(device, DEVICE_TYPES.CURTAIN_MODULE) &&
    hasTrait(device, TRAITS.CURTAIN_TRAVEL_TIME) &&
    (dual || isModel(device, MODELS.W1CM_1));
  // undefined while reading.
  const [times, setTimes] = useState();
  const [motor, setMotor] = useState(0);
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!device || !supported) return undefined;
    let current = true;
    readConfigDevice(device.applianceId).then((fresh) => {
      if (current) setTimes(readTimes(fresh ?? device));
    });
    return () => {
      current = false;
    };
  }, [device, supported]);

  if (!device) return <Redirect to="/dashboard" />;

  const openCalibration = () => {
    setSeconds("");
    setInvalid(false);
    setOpen(true);
  };

  // Sends the travel time in ms for the curtain on screen: id 1, or 2 for
  // W2CM_1's motor 2.
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const value = Number(seconds);
    if (!seconds || !Number.isFinite(value) || value <= 0) {
      toast("Curtain Time is required", "red");
      setInvalid(true);
      document.getElementById(INPUT_ID)?.focus();
      return;
    }
    setOpen(false);
    setBusy("Updating settings…");
    try {
      await sendControl(
        settingsPayload(device, {
          id: dual ? motor + 1 : 1,
          trait: Number(TRAITS.CURTAIN_TRAVEL_TIME),
          value: value * 1000,
        })
      );
      toast(UPDATED, "green");
      if (!alive.current) return;
      setBusy(null);
      goBack(history);
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
    }
  };

  let body;
  if (!supported) {
    body = <Unavailable />;
  } else if (times === undefined) {
    body = <SettingsSkeleton />;
  } else {
    body = (
      <>
        {dual ? <MotorTabs id={TABS_ID} panelId={PANEL_ID} motors={[0, 1]} active={motor} onChange={setMotor} /> : null}
        <section
          id={PANEL_ID}
          className="hx-set-card"
          role={dual ? "tabpanel" : undefined}
          aria-labelledby={dual ? `${TABS_ID}-${motor}` : undefined}
        >
          <button type="button" className="hx-set-row" aria-haspopup="dialog" onClick={openCalibration}>
            <span className="hx-set-row-text">Quick Calibration</span>
            <span className="hx-set-row-value">{times[dual ? motor : 0] / 1000} s</span>
            <IoChevronForward aria-hidden="true" />
          </button>
        </section>
      </>
    );
  }

  return (
    <SettingsPage title="Curtain Settings" device={device} busy={busy}>
      {body}

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title={dual ? `Quick Calibration · Motor ${motor + 1}` : "Quick Calibration"}
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="submit" form={FORM_ID} className="hx-btn">
              Submit
            </button>
          </>
        }
      >
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <ol className="hx-set-steps">
            <li>
              <label htmlFor={INPUT_ID}>Please enter the travel time</label>
              <span className="hx-set-travel">
                <input
                  id={INPUT_ID}
                  className="hx-input hx-set-travel-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="10"
                  value={seconds}
                  aria-invalid={invalid || undefined}
                  aria-describedby="hx-set-curtain-unit"
                  onChange={(event) => {
                    setSeconds(event.target.value.replace(/\D/g, ""));
                    setInvalid(false);
                  }}
                />
                <span id="hx-set-curtain-unit">Seconds</span>
              </span>
            </li>
            <li>Click 'Submit' to complete the calibration</li>
          </ol>
        </form>
      </AppModal>
    </SettingsPage>
  );
}

export default CurtainSettings;
